import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { useWeightEntries } from './useWeightEntries'
import { useMealEntries } from './useMealEntries'
import {
  weeklyWeightSeries, latestRaw, computePace, targetTrajectory,
  dailyProteinMap, dailyStepsMap, trailingAvg, adherenceStreak,
  todayKey, keyOf, PROTEIN_TARGET_G, STEP_TARGET_MIN, STEP_TARGET_MAX,
} from '../lib/goalPace'

const DEFAULT_TARGET_DATE = '2026-07-28'

// useGoalPace — the Goal Pace data layer. Reads existing weigh-ins and food
// logs (no new copies of that data), plus the new daily_steps + settings,
// and runs the pure goalPace math into a ready-to-render view model.
export function useGoalPace() {
  const { user } = useAuth()
  const userId = user?.id || null
  const weight = useWeightEntries()
  const meals = useMealEntries()

  const [settings, setSettings] = useState(null)
  const [steps, setSteps] = useState([])
  const [loadingOwn, setLoadingOwn] = useState(true)

  const refetchOwn = useCallback(async () => {
    if (!userId) { setSettings(null); setSteps([]); setLoadingOwn(false); return }
    const [{ data: s }, { data: st }] = await Promise.all([
      supabase.from('goal_pace_settings').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('daily_steps').select('day, steps').eq('user_id', userId).order('day', { ascending: false }).limit(120),
    ])
    setSettings(s || null)
    setSteps(st || [])
    setLoadingOwn(false)
  }, [userId])

  useEffect(() => { refetchOwn() }, [refetchOwn])

  // Earliest weigh-in — the auto-baseline when the user hasn't set one.
  const earliestWeighIn = useMemo(() => {
    const asc = [...(weight.entries || [])].sort((a, b) => (a.date < b.date ? -1 : 1))
    return asc[0] || null
  }, [weight.entries])

  // Effective settings: stored values, falling back to sensible defaults +
  // the auto-pulled baseline.
  const effectiveSettings = useMemo(() => ({
    target_date: settings?.target_date || DEFAULT_TARGET_DATE,
    baseline_weight_kg: settings?.baseline_weight_kg != null
      ? Number(settings.baseline_weight_kg)
      : (earliestWeighIn ? Number(earliestWeighIn.weight) : null),
    baseline_date: settings?.baseline_date || earliestWeighIn?.date || null,
    goal_weight_kg: settings?.goal_weight_kg != null ? Number(settings.goal_weight_kg) : null,
    step_goal_min: settings?.step_goal_min ?? STEP_TARGET_MIN,
    step_goal_max: settings?.step_goal_max ?? STEP_TARGET_MAX,
    protein_target_g: settings?.protein_target_g ?? PROTEIN_TARGET_G,
  }), [settings, earliestWeighIn])

  const model = useMemo(() => {
    const today = todayKey()
    const weeks = weeklyWeightSeries(weight.entries || [], today)
    const pace = computePace({
      weeks,
      weighInCount: (weight.entries || []).length,
      todayK: today,
      settings: effectiveSettings,
    })

    // Chart series: weekly averages + a sane (band-capped) target line.
    const weekEndKeys = weeks.map((w) => w.weekEndKey)
    const traj = targetTrajectory({
      baselineWeightKg: effectiveSettings.baseline_weight_kg,
      baselineKey: effectiveSettings.baseline_date,
      goalWeightKg: effectiveSettings.goal_weight_kg,
      targetDateKey: effectiveSettings.target_date,
      weekEndKeys,
    })
    const chart = weeks.map((w) => ({
      weekEndKey: w.weekEndKey,
      avg: w.avg,
      target: traj[w.weekEndKey] ?? null,
    }))

    // Levers.
    const proteinMap = dailyProteinMap(meals.entries || [])
    const stepsMap = dailyStepsMap(steps)
    const protein = {
      today: proteinMap[today] ?? null,
      avg7: trailingAvg(proteinMap, today, 7),
      streak: adherenceStreak(proteinMap, today, effectiveSettings.protein_target_g),
      target: effectiveSettings.protein_target_g,
    }
    const stepsLever = {
      today: stepsMap[today] ?? null,
      avg7: trailingAvg(stepsMap, today, 7),
      min: effectiveSettings.step_goal_min,
      max: effectiveSettings.step_goal_max,
    }

    return { pace, chart, protein, steps: stepsLever, rawToday: latestRaw(weight.entries || []) }
  }, [weight.entries, meals.entries, steps, effectiveSettings])

  const saveSettings = useCallback(async (patch) => {
    if (!userId) return { error: 'not signed in' }
    const row = {
      user_id: userId,
      target_date: patch.target_date ?? effectiveSettings.target_date,
      baseline_weight_kg: patch.baseline_weight_kg ?? effectiveSettings.baseline_weight_kg,
      baseline_date: patch.baseline_date ?? effectiveSettings.baseline_date,
      goal_weight_kg: 'goal_weight_kg' in patch ? patch.goal_weight_kg : effectiveSettings.goal_weight_kg,
      step_goal_min: patch.step_goal_min ?? effectiveSettings.step_goal_min,
      step_goal_max: patch.step_goal_max ?? effectiveSettings.step_goal_max,
      protein_target_g: patch.protein_target_g ?? effectiveSettings.protein_target_g,
      updated_at: new Date().toISOString(),
    }
    const { error } = await supabase
      .from('goal_pace_settings')
      .upsert(row, { onConflict: 'user_id' })
    if (error) return { error: error.message }
    await refetchOwn()
    return {}
  }, [userId, effectiveSettings, refetchOwn])

  const setStepsForDay = useCallback(async (stepsValue, day = keyOf(new Date())) => {
    if (!userId) return { error: 'not signed in' }
    const n = Math.max(0, parseInt(stepsValue, 10) || 0)
    // optimistic
    setSteps((prev) => {
      const without = prev.filter((r) => r.day !== day)
      return [{ day, steps: n }, ...without]
    })
    const { error } = await supabase
      .from('daily_steps')
      .upsert({ user_id: userId, day, steps: n, updated_at: new Date().toISOString() }, { onConflict: 'user_id,day' })
    if (error) return { error: error.message }
    await refetchOwn()
    return {}
  }, [userId, refetchOwn])

  return {
    loading: weight.loading || meals.loading || loadingOwn,
    settings: effectiveSettings,
    hasSettingsRow: !!settings,
    ...model,
    saveSettings,
    setStepsForDay,
    refetch: refetchOwn,
  }
}
